import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import Star from "@mui/icons-material/Star";

import { HeldPeriod, serializeHeldPeriods } from "features/trading/heldPeriods";
import {
  Bucket,
  BucketNumberEnum,
  useListBucketsQuery,
} from "features/trading/buckets";
import {
  MAIN_STAR_COLOR,
  sortMembers,
  sortMembersHeldFirst,
} from "./constants";
import { BucketEditModal } from "./BucketEditModal";
import { InlineLoading } from "./InlineLoading";

// Per-chip wording of the editor's held / not-held split. The not-held copy
// says what the editor's disclaimer says (services/bucket_report
// ._sum_pair_bucket_rows leaves such a member out of the bucket's totals),
// phrased for one security rather than as a heading over a list.
const HELD_IN_PERIOD_COPY = "Held in the account during the report period";
const NOT_HELD_IN_PERIOD_COPY =
  "Not held in the account during the report period — disregarded " +
  "during calculations";

// The chip row's flex gap, in theme spacing units. The fit reads the gap's
// pixels off the laid-out row; this fallback is MUI's default 8px spacing.
const ROW_GAP = 0.5;
const ROW_GAP_PX = ROW_GAP * 8;
// Room the fit leaves unused, so sub-pixel rounding in the browser's own
// layout can never push the last item onto a second line.
const FIT_SLACK_PX = 1;

// Marks what each measured item of the row is, so the measuring pass can
// read the chips', the fold control's and the pencil's rendered widths.
const MEASURE = "data-chip-measure";

/**
 * How many leading chips fit on ONE row of `rowWidth` px. `null` when the
 * whole list fits beside the pencil (or the row is not laid out at all), so
 * nothing needs folding; otherwise the count that fits beside the "+N more"
 * control and the pencil, which may be 0 on a very narrow panel — one row is
 * the rule (task 5037).
 */
export const fitChipsOnOneRow = (
  rowWidth: number,
  chipWidths: number[],
  foldWidth: number,
  editWidth: number,
  gap: number = ROW_GAP_PX,
): number | null => {
  if (rowWidth <= 0) return null;
  const room = rowWidth - FIT_SLACK_PX;
  const allChips = chipWidths.reduce((sum, w) => sum + w + gap, 0);
  if (allChips + editWidth <= room) return null;
  let used = foldWidth + gap + editWidth;
  let count = 0;
  while (count < chipWidths.length && used + chipWidths[count] + gap <= room) {
    used += chipWidths[count] + gap;
    count += 1;
  }
  return count;
};

// Exact (fractional) rendered width — `offsetWidth` rounds to whole pixels,
// and a row of rounded-down chips can overflow the row it was fitted to.
const widthOf = (el: Element) => el.getBoundingClientRect().width;

interface AccountBucketSummaryProps {
  accountId: number | null | undefined;
  accountLabel: string;
  number: BucketNumberEnum;
  // The report's periods: the editor's asset picker offers only securities
  // the account held across them, and its member list is split by whether
  // each member was held inside them.
  heldPeriods?: HeldPeriod[];
}

/**
 * Compact read-only view of an account's Bucket 1/2 for the report settings:
 * member chips (main assets starred), "Not created" when absent. With the
 * report's periods, members held in them come first and are highlighted —
 * the same held / not-held split the editor's member list draws. Folded, the
 * chips take one row: as many as fit at the panel's current width, then
 * "+N more" and the pencil on that same row; "Show less" folds it back.
 * Clicking the chips or the pencil opens the shared BucketEditModal to
 * add/remove assets.
 */
export const AccountBucketSummary = ({
  accountId,
  accountLabel,
  number,
  heldPeriods,
}: AccountBucketSummaryProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Unfolded for ONE account: whose list is open, not whether a list is. A
  // different account's bucket therefore starts folded with no reset code.
  const [expandedFor, setExpandedFor] = useState<typeof accountId>(null);
  const isExpanded = expandedFor != null && expandedFor === accountId;
  const chipsId = useId();
  const rowRef = useRef<HTMLDivElement>(null);
  // The fold is measured on the real chips: a measuring pass lays every chip,
  // the widest "+N more" and the pencil out on one unwrapped row, reads their
  // widths before the browser paints, and caches them — so a resize refits
  // from the cache without laying the list out again.
  const [isMeasuring, setIsMeasuring] = useState(true);
  const widths = useRef<{
    chips: number[];
    fold: number;
    edit: number;
    gap: number;
  }>();
  // How many chips fit on the folded row; null = all of them (no fold).
  const [fitCount, setFitCount] = useState<number | null>(null);
  const heldPeriodsParam = useMemo(
    () => serializeHeldPeriods(heldPeriods),
    [heldPeriods],
  );
  const {
    data: bucketsData,
    currentData,
    isFetching,
  } = useListBucketsQuery(
    {
      account: accountId as number,
      ...(heldPeriodsParam ? { held_periods: heldPeriodsParam } : {}),
    },
    { skip: !accountId },
  );
  // Split the chips held / not held — the editor's own split — only once
  // the answer on screen was fetched for THESE periods. Without periods every
  // member's `held_in_period` is False, which would grey out the whole
  // bucket on a screen that never asked about a period; and while a period
  // change is in flight the chips show the previous answer (below, and RTK's
  // own `data`), whose flags describe the OLD periods. `currentData` is set
  // only for the current query args, so until it lands the chips fall back
  // to the plain alphabetical, uncoloured view rather than state a status
  // for a period nobody asked about.
  const hasPeriods = !!heldPeriodsParam && !!currentData;
  const fetched = bucketsData?.results.find((b) => b.number === number);
  // Hold the last answer while a new one is in flight. The query is keyed on
  // the report's periods, so every date edit is a NEW cache entry with no
  // data yet — without this the member chips would blink to a loading hint
  // on every period change, which they never did when the list was keyed on
  // the account alone. The membership does not depend on the period, so the
  // previous answer's members are the right ones to keep showing; only the
  // per-member `held_in_period` flag is period-specific, which is why the
  // held / not-held split above waits for `currentData`. Reset on an account switch, where
  // the held answer belongs to a different account and is simply wrong.
  const held = useRef<{ accountId: typeof accountId; bucket?: Bucket }>({
    accountId,
  });
  if (held.current.accountId !== accountId) held.current = { accountId };
  if (fetched) held.current.bucket = fetched;
  const bucket = fetched ?? held.current.bucket;
  // On first load / account switch there is nothing to show yet — a loading
  // hint instead of a misleading "Not created".
  const isLoadingBuckets = isFetching && !bucket;
  const members = useMemo(
    () =>
      bucket
        ? hasPeriods
          ? sortMembersHeldFirst(bucket.assets)
          : sortMembers(bucket.assets)
        : [],
    [bucket, hasPeriods],
  );

  // A new list (or a different account, or the list arriving) is measured
  // afresh, before paint, so the unfolded list never flashes.
  useLayoutEffect(() => {
    setIsMeasuring(true);
  }, [members, accountId, isLoadingBuckets]);

  // The web font landing re-sizes every chip: measure once more then.
  useEffect(() => {
    let live = true;
    document.fonts?.ready.then(() => live && setIsMeasuring(true));
    return () => {
      live = false;
    };
  }, []);

  const refit = () => {
    const row = rowRef.current;
    if (!row || !widths.current) return;
    const { chips, fold, edit, gap } = widths.current;
    const rowWidth = widthOf(row);
    // Measured while not laid out (e.g. inside a hidden ancestor): every
    // width read 0, so measure again now that the row has a width.
    if (rowWidth > 0 && fold === 0) {
      setIsMeasuring(true);
      return;
    }
    setFitCount(fitChipsOnOneRow(rowWidth, chips, fold, edit, gap));
  };

  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!isMeasuring) return;
    if (row) {
      const widthsOf = (kind: string) =>
        Array.from(row.querySelectorAll(`[${MEASURE}="${kind}"]`)).map(widthOf);
      const gap = parseFloat(getComputedStyle(row).columnGap);
      widths.current = {
        chips: widthsOf("chip"),
        fold: widthsOf("fold")[0] ?? 0,
        edit: widthsOf("edit")[0] ?? 0,
        gap: Number.isFinite(gap) ? gap : ROW_GAP_PX,
      };
      refit();
    }
    setIsMeasuring(false);
  });

  // The panel's width changes: refit from the cached widths.
  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => refit());
    observer.observe(row);
    return () => observer.disconnect();
    // The row exists only once an account's list is shown.
  }, [accountId, isLoadingBuckets]);

  if (!accountId) {
    return (
      <Typography variant="body2" color="text.disabled">
        Select an account
      </Typography>
    );
  }

  if (isLoadingBuckets) {
    return <InlineLoading />;
  }

  const isCollapsible = !isMeasuring && fitCount !== null;
  const hiddenCount = members.length - (fitCount ?? members.length);
  const shownMembers =
    isCollapsible && !isExpanded ? members.slice(0, fitCount) : members;

  const editButton = (
    <Tooltip title={`Edit Bucket ${number}`}>
      <IconButton
        size="small"
        sx={{ flexShrink: 0 }}
        onClick={() => setIsModalOpen(true)}
        {...{ [MEASURE]: "edit" }}
      >
        <EditIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
  // Nothing on the row shrinks, so the measuring pass reads each item at
  // the width it is painted at.
  const foldLabelSx = {
    textTransform: "none",
    px: 0.5,
    minWidth: 0,
    flexShrink: 0,
    whiteSpace: "nowrap",
  } as const;
  // Each chip sits in its own non-shrinking span: the measuring pass reads
  // its natural width there, and the Tooltip anchors on it.
  const chipFor = (member: (typeof members)[number]) => (
    <Box
      key={member.id}
      component="span"
      display="inline-flex"
      maxWidth="100%"
      flexShrink={0}
      {...{ [MEASURE]: "chip" }}
    >
      <Chip
        size="small"
        label={member.symbol}
        color={hasPeriods && member.held_in_period ? "primary" : "default"}
        icon={
          member.is_main ? (
            <Star fontSize="small" htmlColor={MAIN_STAR_COLOR} />
          ) : undefined
        }
      />
    </Box>
  );

  return (
    <>
      <Box
        ref={rowRef}
        id={chipsId}
        display="flex"
        alignItems="center"
        gap={ROW_GAP}
        // Measuring, the row is one unwrapped line clipped at the panel —
        // never painted, as the pass completes before paint.
        flexWrap={isMeasuring ? "nowrap" : "wrap"}
        overflow={isMeasuring ? "hidden" : undefined}
        sx={{ cursor: "pointer" }}
        onClick={() => setIsModalOpen(true)}
      >
        {!bucket ? (
          <Chip size="small" variant="outlined" label="Not created" />
        ) : members.length === 0 ? (
          <Chip size="small" variant="outlined" label="Empty" />
        ) : (
          shownMembers.map((member) =>
            hasPeriods ? (
              <Tooltip
                key={member.id}
                title={
                  member.held_in_period
                    ? HELD_IN_PERIOD_COPY
                    : NOT_HELD_IN_PERIOD_COPY
                }
              >
                {chipFor(member)}
              </Tooltip>
            ) : (
              chipFor(member)
            ),
          )
        )}
        {/* The fold control follows the chips on their own row, then the
            pencil, so a folded list is exactly one line. It toggles the
            fold without opening the editor the row's click opens. While
            measuring, the SAME button carries its widest label, so a
            remeasure never drops a keyboard user's focus. */}
        {(isMeasuring || isCollapsible) && (
          <Button
            size="small"
            aria-expanded={isExpanded}
            aria-controls={chipsId}
            aria-label={
              isMeasuring
                ? undefined
                : isExpanded
                  ? `Show fewer Bucket ${number} securities`
                  : `Show ${hiddenCount} more Bucket ${number} securities`
            }
            onClick={(event) => {
              event.stopPropagation();
              setExpandedFor(isExpanded ? null : accountId);
            }}
            sx={foldLabelSx}
            {...{ [MEASURE]: "fold" }}
          >
            {isMeasuring
              ? `+${members.length} more`
              : isExpanded
                ? "Show less"
                : `+${hiddenCount} more`}
          </Button>
        )}
        {editButton}
      </Box>
      <BucketEditModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        accountId={accountId}
        accountLabel={accountLabel}
        number={number}
        bucket={bucket}
        heldPeriods={heldPeriods}
        // This summary is the report settings' own entry point into the
        // editor, so the holdings status is shown here and nowhere else.
        showHoldingStatus
      />
    </>
  );
};
